import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { DOCUMENTS_ROUTING_PARAMS, DocumentsPaths } from 'app/configs/routesConfig/documents';
import { documentsApi } from 'app/services/api';

import { getFileDateTimeName, printFileFromBlob } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';
import { openURLInNewTab } from 'utils/navigation';

import { showErrorMessageAction } from 'store/global/actions';
import { selectDocumentTypeNameByDocumentId } from 'store/system/selectors';

/**
 * Download / print / open-in-new-tab handlers for a single document, shared by the
 * Documents tab and the loan-editor document side-panes so the logic lives in one place.
 *
 * @param {string} documentId
 * @returns {{ onDownloadDocument: Function, onPrintDocument: Function, onOpenDocumentInNewTab: Function }}
 */
export const useDocumentActions = documentId => {
  const dispatch = useDispatch();
  const history = useHistory();

  const documentTypeName = useSelector(selectDocumentTypeNameByDocumentId(documentId));

  const onDownloadDocument = useCallback(async () => {
    try {
      const fileName = getFileDateTimeName({ fileName: documentTypeName, extension: 'pdf' });

      const { data: pdfBlob } = await documentsApi.getContent({ documentId });

      await downloadOrShareBlob(pdfBlob, fileName);
    } catch (err) {
      dispatch(showErrorMessageAction({ message: 'Could not download document', err }));
      logger.error(err);
    }
  }, [dispatch, documentId, documentTypeName]);

  const onPrintDocument = useCallback(() => {
    documentsApi
      .getContent({ documentId })
      .then(res => printFileFromBlob(res.data))
      .catch(err => {
        dispatch(showErrorMessageAction({ message: 'Could not print document', err }));
        logger.error(err);
      });
  }, [dispatch, documentId]);

  const onOpenDocumentInNewTab = useCallback(
    (docId = documentId) => {
      const href = history.createHref({
        pathname: DocumentsPaths.documentPreview.generatePath({
          [DOCUMENTS_ROUTING_PARAMS.docId]: docId,
        }),
      });

      openURLInNewTab(href);
    },
    [history, documentId],
  );

  return { onDownloadDocument, onPrintDocument, onOpenDocumentInNewTab };
};
